import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { apiError } from "../api";
import { PageHeader, Card, Btn, Input, Textarea, Field, Badge, Spinner, Empty, RequireTenant } from "../ui";
import { AjutorButton } from "../AjutorButton";
import { usePermissions } from "../AuthContext";
import {
  Users, X, StickyNote, Activity, MousePointerClick, Search, Trash2, Plus, Upload,
} from "lucide-react";
import { toast } from "sonner";

const STAGES = ["nou", "contactat", "calificat", "client", "pierdut"];
const STAGE_COLOR = { nou: "slate", contactat: "blue", calificat: "amber", client: "green", pierdut: "red" };
const KIND_LABEL = { apel: "Apel", email: "Email", intalnire: "Intalnire", sarcina: "Sarcina", nota: "Nota" };

function fmt(dt) {
  if (!dt) return "-";
  try { return new Date(dt).toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" }); }
  catch { return dt; }
}
function fmtDate(dt) {
  if (!dt) return "-";
  try { return new Date(dt).toLocaleDateString("ro-RO", { dateStyle: "short" }); }
  catch { return dt; }
}

function Stat({ label, value, testid }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-1 text-2xl font-bold text-slate-900">{value}</div>
    </Card>
  );
}

function DetailDrawer({ contactId, onClose, onChanged }) {
  const { can } = usePermissions();
  const canEdit = can("contacte", "edit");
  const canDelete = can("contacte", "delete");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState("");
  const [actKind, setActKind] = useState("apel");
  const [actText, setActText] = useState("");
  const [actDue, setActDue] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/tenant-crm/contacts/${contactId}`); setData(data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [contactId]);
  useEffect(() => { load(); }, [load]);

  const setStage = async (stage) => {
    try { await api.patch(`/tenant-crm/contacts/${contactId}/stage`, { stage }); toast.success("Stadiu actualizat"); load(); onChanged && onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const addNote = async () => {
    if (!noteText.trim()) return;
    try { await api.post(`/tenant-crm/contacts/${contactId}/notes`, { text: noteText }); setNoteText(""); toast.success("Notita adaugata"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const delNote = async (id) => {
    try { await api.delete(`/tenant-crm/contacts/${contactId}/notes/${id}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const addAct = async () => {
    if (!actText.trim()) return;
    try { await api.post(`/tenant-crm/contacts/${contactId}/activities`, { kind: actKind, text: actText, due_at: actDue || null }); setActText(""); setActDue(""); toast.success("Activitate adaugata"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const c = data?.contact;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-xl h-full bg-white overflow-auto shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="tcrm-drawer">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900 truncate">{c?.company || c?.name || c?.email || "Lead"}</h3>
          <button onClick={onClose} data-testid="tcrm-drawer-close"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        {loading || !c ? <div className="p-8"><Spinner /></div> : (
          <div className="p-5 space-y-6">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-slate-400">Email</span><div className="font-medium break-all">{c.email || "-"}</div></div>
              <div><span className="text-slate-400">Telefon</span><div className="font-medium">{c.phone || "-"}</div></div>
              <div><span className="text-slate-400">Nume</span><div className="font-medium">{c.name || "-"}</div></div>
              <div><span className="text-slate-400">Companie</span><div className="font-medium">{c.company || "-"}</div></div>
              <div><span className="text-slate-400">Site</span><div className="font-medium break-all">{c.website || "-"}</div></div>
              <div><span className="text-slate-400">Tara</span><div className="font-medium">{c.country || "-"}</div></div>
              <div><span className="text-slate-400">Judet</span><div className="font-medium">{c.county || "-"}</div></div>
              <div><span className="text-slate-400">Localitate</span><div className="font-medium">{c.locality || "-"}</div></div>
              <div><span className="text-slate-400">Sursa</span><div className="font-medium">{c.source || "-"}</div></div>
              <div><span className="text-slate-400">Campanie</span><div className="font-medium">{c.campaign || "-"}</div></div>
              <div><span className="text-slate-400">Clicuri</span><div className="font-medium">{c.click_count || 0}</div></div>
              <div><span className="text-slate-400">Ultimul clic</span><div className="font-medium">{fmtDate(c.last_click_at)}</div></div>
              <div><span className="text-slate-400">Data intrare</span><div className="font-medium">{fmtDate(c.created_at)}</div></div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Stadiu vanzare</div>
              <div className="flex flex-wrap gap-2">
                {STAGES.map((s) => (
                  <button key={s} onClick={() => setStage(s)} data-testid={`tcrm-stage-${s}`} disabled={!canEdit}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${c.stage === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2 text-slate-700 font-semibold mb-2"><Activity className="w-4 h-4" /> Activitati</div>
              {canEdit && (
              <div className="rounded-lg border border-slate-200 p-3 mb-3 space-y-2">
                <div className="flex gap-2">
                  <select value={actKind} onChange={(e) => setActKind(e.target.value)} data-testid="tcrm-activity-kind"
                    className="px-2 py-2 text-sm rounded-lg border border-slate-200 outline-none focus:border-emerald-500">
                    {Object.entries(KIND_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                  <Input type="date" value={actDue} onChange={(e) => setActDue(e.target.value)} data-testid="tcrm-activity-due" className="w-40" />
                </div>
                <div className="flex flex-col gap-2">
                  <Textarea value={actText} onChange={(e) => setActText(e.target.value)} placeholder="Descriere activitate" data-testid="tcrm-activity-text" />
                  <Btn onClick={addAct} data-testid="tcrm-activity-add" className="w-full sm:w-auto sm:self-end">Adauga</Btn>
                </div>
              </div>
              )}
              <div className="space-y-2 max-h-64 overflow-auto" data-testid="tcrm-activities-list">
                {(data.activities || []).length === 0 ? <p className="text-sm text-slate-400">Nicio activitate.</p> :
                  data.activities.map((a, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm">
                      <span className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${a.manual ? "bg-emerald-500" : "bg-slate-300"}`} />
                      <div className="flex-1">
                        <div className="text-slate-800">
                          {a.manual && <span className="font-medium mr-1">{KIND_LABEL[a.kind] || a.kind}:</span>}
                          {a.label}
                          {a.due_at && <span className="text-amber-600"> (termen {fmtDate(a.due_at)})</span>}
                        </div>
                        <div className="text-xs text-slate-400">{fmt(a.at)}</div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2 text-slate-700 font-semibold mb-2"><StickyNote className="w-4 h-4" /> Notite</div>
              {canEdit && (
              <div className="flex flex-col gap-2 mb-3">
                <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Scrie o notita" data-testid="tcrm-note-text" />
                <Btn onClick={addNote} data-testid="tcrm-note-add" className="w-full sm:w-auto sm:self-end">Adauga</Btn>
              </div>
              )}
              <div className="space-y-2" data-testid="tcrm-notes-list">
                {(data.notes || []).length === 0 ? <p className="text-sm text-slate-400">Nicio notita.</p> :
                  data.notes.map((n) => (
                    <div key={n.id} className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-slate-800 whitespace-pre-wrap flex-1">{n.text}</p>
                        <button onClick={() => delNote(n.id)} data-testid={`tcrm-note-del-${n.id}`} style={{ display: canDelete ? undefined : "none" }}><Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" /></button>
                      </div>
                      <div className="text-xs text-slate-400 mt-1">{n.author} · {fmt(n.created_at)}</div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AddLeadModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ email: "", name: "", company: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/contacts", form);
      toast.success("Lead adaugat");
      onSaved();
    } catch (err) { toast.error(apiError(err, "Nu s-a putut adauga")); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Lead nou</h3>
          <button onClick={onClose} data-testid="tcrm-add-close"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <form onSubmit={save} data-testid="tcrm-add-form">
          <Field label="Email *"><Input data-testid="tcrm-lead-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          <Field label="Nume"><Input data-testid="tcrm-lead-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Companie"><Input data-testid="tcrm-lead-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></Field>
          <Field label="Telefon"><Input data-testid="tcrm-lead-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
          <div className="flex justify-end gap-2 mt-2">
            <Btn variant="outline" type="button" onClick={onClose}>Anuleaza</Btn>
            <Btn type="submit" disabled={saving} data-testid="tcrm-lead-save">Salveaza</Btn>
          </div>
        </form>
      </Card>
    </div>
  );
}

function TenantCrmInner() {
  const { can } = usePermissions();
  const canAdd = can("contacte", "add");
  const [ov, setOv] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [sel, setSel] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const fileRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, c] = await Promise.all([api.get("/tenant-crm/overview"), api.get("/tenant-crm/contacts")]);
      setOv(o.data); setItems(c.data.items || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/contacts/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Import: ${data.imported} noi, ${data.duplicate_email + data.duplicate_domain} duplicate, ${data.invalid} invalide`);
      load();
    } catch (err) { toast.error(apiError(err, "Import esuat")); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const filtered = items.filter((r) => {
    const okQ = !q || (r.company || "").toLowerCase().includes(q.toLowerCase()) || (r.name || "").toLowerCase().includes(q.toLowerCase()) || (r.email || "").toLowerCase().includes(q.toLowerCase());
    const okS = !stageFilter || r.stage === stageFilter;
    return okQ && okS;
  });

  return (
    <div>
      {loading ? <Spinner /> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Stat label="Lead-uri" value={ov?.total ?? 0} testid="tcrm-stat-total" />
            <Stat label="Au dat clic" value={ov?.clicked ?? 0} testid="tcrm-stat-clicked" />
            <Stat label="Clienti" value={ov?.by_stage?.client ?? 0} testid="tcrm-stat-clienti" />
            <Stat label="Conversie" value={`${ov?.conversion_rate ?? 0}%`} testid="tcrm-stat-conversion" />
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cauta dupa firma, nume sau email" className="pl-9" data-testid="tcrm-search" />
            </div>
            <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} data-testid="tcrm-stage-filter"
              className="px-3 py-2 text-sm rounded-lg border border-slate-200 outline-none focus:border-emerald-500">
              <option value="">Toate stadiile</option>
              {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            {canAdd && (
              <>
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={doImport} data-testid="tcrm-import-input" />
                <Btn variant="outline" onClick={() => fileRef.current?.click()} data-testid="tcrm-import-button"><Upload className="w-4 h-4" /> Import lista (CSV / Excel)</Btn>
                <Btn onClick={() => setShowAdd(true)} data-testid="tcrm-add-button"><Plus className="w-4 h-4" /> Adauga lead</Btn>
              </>
            )}
          </div>

          {filtered.length === 0 ? (
            <Card><Empty icon={Users} title="Niciun lead" hint="Adauga manual un lead, importa o lista CSV sau asteapta firmele care dau clic pe linkul din campania ta." /></Card>
          ) : (
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm whitespace-nowrap" data-testid="tcrm-table">
                  <thead>
                    <tr className="text-left text-slate-400 border-b border-slate-200">
                      <th className="px-4 py-3 font-medium">Firma</th>
                      <th className="px-4 py-3 font-medium">Nume</th>
                      <th className="px-4 py-3 font-medium">Email</th>
                      <th className="px-4 py-3 font-medium">Telefon</th>
                      <th className="px-4 py-3 font-medium">Site</th>
                      <th className="px-4 py-3 font-medium">Tara</th>
                      <th className="px-4 py-3 font-medium">Judet</th>
                      <th className="px-4 py-3 font-medium">Localitate</th>
                      <th className="px-4 py-3 font-medium">Sursa</th>
                      <th className="px-4 py-3 font-medium text-center">Clic</th>
                      <th className="px-4 py-3 font-medium">Ultimul clic</th>
                      <th className="px-4 py-3 font-medium">Intrare</th>
                      <th className="px-4 py-3 font-medium">Stadiu</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r) => (
                      <tr key={r.id} onClick={() => setSel(r.id)} data-testid={`tcrm-row-${r.id}`}
                        className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer">
                        <td className="px-4 py-3 font-medium text-slate-900">{r.company || "-"}</td>
                        <td className="px-4 py-3 text-slate-600">{r.name || "-"}</td>
                        <td className="px-4 py-3 text-slate-600">{r.email || "-"}</td>
                        <td className="px-4 py-3 text-slate-600">{r.phone || "-"}</td>
                        <td className="px-4 py-3 text-slate-600 break-all" data-testid={`tcrm-cell-website-${r.id}`}>{r.website || "-"}</td>
                        <td className="px-4 py-3 text-slate-600" data-testid={`tcrm-cell-country-${r.id}`}>{r.country || "-"}</td>
                        <td className="px-4 py-3 text-slate-600" data-testid={`tcrm-cell-county-${r.id}`}>{r.county || "-"}</td>
                        <td className="px-4 py-3 text-slate-600" data-testid={`tcrm-cell-locality-${r.id}`}>{r.locality || "-"}</td>
                        <td className="px-4 py-3 text-slate-600">{r.source || "-"}</td>
                        <td className="px-4 py-3 text-center">
                          {r.click_count > 0
                            ? <span className="inline-flex items-center gap-1 text-emerald-600 font-medium"><MousePointerClick className="w-4 h-4" />{r.click_count}</span>
                            : <span className="text-slate-300">-</span>}
                        </td>
                        <td className="px-4 py-3 text-slate-600">{fmtDate(r.last_click_at)}</td>
                        <td className="px-4 py-3 text-slate-600">{fmtDate(r.created_at)}</td>
                        <td className="px-4 py-3"><Badge color={STAGE_COLOR[r.stage] || "slate"}>{r.stage}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}

      {sel && <DetailDrawer contactId={sel} onClose={() => setSel(null)} onChanged={load} />}
      {showAdd && <AddLeadModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

export default function TenantCrm() {
  return (
    <div>
      <PageHeader title="CRM" subtitle="Firmele care dau clic pe linkul din campania ta. Gestioneaza-le ca lead-uri." />
      <AjutorButton file="ajutor_contacte.pdf" />
      <RequireTenant><TenantCrmInner /></RequireTenant>
    </div>
  );
}
